import { getTrackedChangesPluginDOMAttrs } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { nodeTrackedChangeAttrsToDOMAttrs } from '@common/util/schema';
import { syncDomAttrs } from '@portal-core/general/util/sync-dom-attrs';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class FootnoteNodeView extends NodeViewBase implements NodeView {
  /** A map of applied button attributes used to track attr syncing with syncDomAttrs. */
  private appliedButtonAttrs = new Map<string, any>();
  /** A map of applied content attributes used to track attr syncing with syncDomAttrs. */
  private appliedContentAttrs = new Map<string, any>();
  private buttonDOM: HTMLButtonElement;
  private onPopoverBeforeToggle: (event: ToggleEvent) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-footnote-button');
    this.buttonDOM.popoverTargetAction = 'toggle';
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-footnote-content'));
    this.contentDOM.setAttribute('contenteditable', 'false');
    this.contentDOM.setAttribute('popover', 'auto');

    this.buttonDOM.popoverTargetElement = this.contentDOM;

    // Listen for beforetoggle events to toggle contenteditable on the footnote content
    this.onPopoverBeforeToggle = event => {
      if (event.newState === 'open') {
        this.contentDOM.setAttribute('contenteditable', 'true');
      } else {
        this.contentDOM.setAttribute('contenteditable', 'false');
      }
    };

    this.contentDOM.addEventListener('beforetoggle', this.onPopoverBeforeToggle);

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode(node, decorations);
  }

  destroy() {
    this.contentDOM.removeEventListener('beforetoggle', this.onPopoverBeforeToggle);
    super.destroy();
  }

  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    if (dir < 0) {
      return pos;
    }

    // If the footnote popover is open, navigate to the start of the footnote content
    const isOpen = this.contentDOM.matches(':popover-open');
    if (isOpen) {
      return pos + 1;
    }

    // Popover is closed - navigate past the footnote
    return pos + this.node.nodeSize;
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }

  syncNode(node: ProseMirrorNode, decorations: Decoration[]) {
    const trackedChangesAttrs = {
      ...nodeTrackedChangeAttrsToDOMAttrs(node),
      ...getTrackedChangesPluginDOMAttrs(decorations)
    };

    // Add the footnote's tracked change DOM attrs to the content and body so that tracked change styles are applied to them as well
    syncDomAttrs(this.buttonDOM, trackedChangesAttrs, this.appliedButtonAttrs);
    syncDomAttrs(this.contentDOM, trackedChangesAttrs, this.appliedContentAttrs);
  }
}
